import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import Modal from '../UI/Modal'
import { useT } from '../../hooks/useTranslation'
import { useDashboardStore } from '../../store/dashboardStore'
import { getSubscriptionPlans, createPaymentRequest, getPaymentStatus, confirmPaymentRequest, getMe, cancelPaymentRequest, sendSupportMessage, getSubscriptionStarsInvoice } from '../../services/api'
import ConfirmModal from '../common/ConfirmModal'
import { Check, Copy, Loader2, Clock, Network, Bot } from 'lucide-react'

// Сети приёма оплаты. Одна монета — USDT; пользователь выбирает только сеть.
// Ключи = как в БД / API (deposit_addresses / payment_requests.network).
const NETWORKS = [
  { key: 'TRC20',    name: 'TRC-20', sub: '(TRON)' },
  { key: 'BEP20',    name: 'BEP-20', sub: '(BSC)'  },
  { key: 'SOL_USDT', name: 'SOL',    sub: '(Solana)' },
  { key: 'TON',      name: 'TON',    sub: '(GRAM)' },
]

// Связка «сеть оплаты» → ключ в конфиге приложения (admin_config.json).
// Админ может выключить способ оплаты во вкладке «Админ» — сеть скроется.
const NETWORK_PAYMENT_KEY = { TRC20: 'usdt_trc20', BEP20: 'usdt_bep20', SOL_USDT: 'usdt_sol', TON: 'ton', ETH: 'eth' }

const PLAN_LABEL_KEYS = { demo: 'planDemo', real_1m: 'plan1Month', real_3m: 'plan3Months', real_6m: 'plan6Months' }

const POLL_SEC = 5000

// Повторная принудительная проверка сети после «Я отправил платёж»:
// платёж может дойти через минуту после нажатия, а проверка была одноразовой
// (и фоновый монитор может быть не запущен). Пока окно «оплата в обработке»
// открыто, каждые VERIFY_RETRY_MS снова зовём POST /confirm — сервер заново
// опрашивает сеть заявки. Ограничено MAX_VERIFY_RETRIES (~2 минуты), чтобы
// не молотить бесконечно.
const VERIFY_RETRY_MS = 12000
const MAX_VERIFY_RETRIES = 10

// Поддержка: сюда направляются обращения пользователей (Telegram).
// Остальная логика поддержки будет реализована позже — сейчас просто
// собираем сообщение с контекстом заявки и открываем чат.
const SUPPORT_TELEGRAM = 'https://t.me/fund1ngsupport'

export default function SubscriptionModal({ onClose }) {
  const t = useT()
  const language = useDashboardStore(s => s.language)
  const setSubscriptionEnd = useDashboardStore(s => s.setSubscriptionEnd)
  const setAccessFlags = useDashboardStore(s => s.setAccessFlags)
  // Feature-флаг способов оплаты из конфига приложения (админка): выключенные
  // админом сети не показываем в выборе.
  const appConfig = useDashboardStore(s => s.appConfig)

  const [step, setStep] = useState(1)
  const [plans, setPlans] = useState([])
  const [networks, setNetworks] = useState({})
  const [loadError, setLoadError] = useState(null)

  const [selectedPlan, setSelectedPlan] = useState(null)
  const [selectedNetwork, setSelectedNetwork] = useState(null)

  // Telegram Stars («Покупка в один клик», план 2026-09-05). Фазы:
  // null (обычный шаг 2) → 'busy' (счёт открыт, ждём закрытия) →
  // 'activating' (оплачено, ждём активацию бэкендом) → 'paid' (успех).
  // Ошибка открытия счёта — в starsError, фаза при этом возвращается к null.
  const [starsPhase, setStarsPhase] = useState(null)
  const [starsError, setStarsError] = useState(null)
  const [starsSubEnd, setStarsSubEnd] = useState(null)
  // Защита от двойного тапа до ре-рендера + единожды отработавший settle.
  const starsBusyRef = useRef(false)
  const starsSettledRef = useRef(false)
  const starsClosedRef = useRef(null)      // текущий обработчик invoiceClosed

  // Созданная заявка на оплату (ответ POST /api/subscription/payment)
  const [payment, setPayment] = useState(null)
  const [paymentError, setPaymentError] = useState(null)
  const [creatingPayment, setCreatingPayment] = useState(false)
  const [status, setStatus] = useState(null)      // pending | paid | expired
  const [checking, setChecking] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(null)
  const [copied, setCopied] = useState(false)
  const [copiedAmount, setCopiedAmount] = useState(false)
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [pendingHint, setPendingHint] = useState(null)   // фидбек после «Я отправил платёж»
  // Окно «оплата в обработке» (открывается по «Я отправил платёж»)
  const [processingWindow, setProcessingWindow] = useState(false)
  const [subEnd, setSubEnd] = useState(null)              // дата окончания подписки (шаг 4)
  // Подтверждение закрытия окна «оплата в обработке» (кнопка «Отмена»).
  const [confirmCloseWindow, setConfirmCloseWindow] = useState(false)
  // Окно поддержки (появляется после истечения заявки)
  const [showSupport, setShowSupport] = useState(false)
  const [supportOption, setSupportOption] = useState(null)
  const [supportText, setSupportText] = useState('')
  const [supportCopied, setSupportCopied] = useState(false)
  const [supportSending, setSupportSending] = useState(false)
  const [supportSent, setSupportSent] = useState(false)

  const modalRef = useRef(null)
  const pollRef = useRef(null)
  const timerRef = useRef(null)
  const checkingRef = useRef(false)
  const pollStatusRef = useRef(null)
  const verifyRetriesRef = useRef(0)

  // ── Загрузка тарифов + свободных адресов ────────────────────────────────
  useEffect(() => {
    getSubscriptionPlans()
      .then(data => {
        setPlans(data.plans || [])
        setNetworks(data.networks || {})
      })
      .catch(err => setLoadError(err.message))
  }, [])

  const freeCount = (networkKey) => networks[networkKey] || 0

  // Сеть доступна, если есть свободный адрес.
  const networkAvailable = (networkKey) => freeCount(networkKey) > 0

  // ── Создание заявки на оплату ───────────────────────────────────────────
  const createPayment = async () => {
    if (!selectedPlan || !selectedNetwork) return
    setCreatingPayment(true)
    setPaymentError(null)
    setStatus(null)
    setPendingHint(null)
    setProcessingWindow(false)
    setSubEnd(null)
    try {
      const data = await createPaymentRequest(selectedPlan.code, selectedNetwork)
      setPayment(data)
      setSecondsLeft(Math.max(0, Math.floor((new Date(data.expiresAt.replace(' ', 'T') + 'Z').getTime() - Date.now()) / 1000)))
    } catch (err) {
      const msg = err.message || ''
      if (msg.includes('No free deposit address')) setPaymentError(t.noFreeAddress)
      else if (msg.includes('rate')) setPaymentError(t.rateUnavailable)
      else setPaymentError(`${t.paymentCreateFailed} ${msg}`)
    } finally {
      setCreatingPayment(false)
    }
  }

  // ── Telegram Stars: «Покупка в один клик» (план 2026-09-05) ────────────────
  // Счёт открывается WebApp.openInvoice; исход (paid/cancelled/failed) приходит
  // событием invoiceClosed И колбэком/промисом openInvoice (версии SDK разные).
  // settleStars защищён флагом — сработает только первый исход.
  const detachStarsListener = () => {
    try {
      const tg = window.Telegram?.WebApp
      if (starsClosedRef.current && tg && typeof tg.offEvent === 'function') {
        tg.offEvent('invoiceClosed', starsClosedRef.current)
      }
    } catch { /* некритично */ }
    starsClosedRef.current = null
  }

  // Оплачено: Telegram уже прислал successful_payment боту — он активирует
  // подписку и пишет журнал. Короткий опрос getMe до появления новой даты
  // окончания; если бэкенд не успел за ~12с — успех по факту оплаты, дата
  // подтянется в шапке профиля при следующем getMe.
  const activateAfterStarsPaid = async () => {
    for (let i = 0; i < 12; i++) {
      try {
        const me = await getMe()
        if (me && me.id != null) setAccessFlags(me)
        if (me && me.subscription_end) {
          setSubscriptionEnd(me.subscription_end)
          setStarsSubEnd(me.subscription_end)
          setStarsPhase('paid')
          return
        }
      } catch { /* сеть моргнула — пробуем ещё */ }
      await new Promise(r => setTimeout(r, 1000))
    }
    setStarsPhase('paid')
  }

  const settleStars = (status, invoiceLink) => {
    if (starsSettledRef.current) return
    starsSettledRef.current = true
    starsBusyRef.current = false
    detachStarsListener()
    if (status === 'paid') {
      setStarsPhase('activating')
      activateAfterStarsPaid()
      return
    }
    // cancelled | failed — возвращаемся к выбору сети (тариф остаётся выбранным).
    setStarsPhase(null)
  }

  const handleStarsBuy = async () => {
    if (!selectedPlan || starsPhase !== null || starsBusyRef.current) return
    const tg = window.Telegram?.WebApp
    if (!tg || typeof tg.openInvoice !== 'function') {
      // Приложение открыто не из Telegram — окно оплаты открыть нельзя.
      setStarsError(t.starsNotInTelegram)
      return
    }
    starsBusyRef.current = true
    starsSettledRef.current = false
    setStarsError(null)
    setStarsPhase('busy')

    let invoiceLink = ''
    try {
      const data = await getSubscriptionStarsInvoice(selectedPlan.code)
      if (!data || !data.invoiceLink) throw new Error(t.starsInvoiceError)
      invoiceLink = data.invoiceLink
    } catch (err) {
      starsBusyRef.current = false
      setStarsPhase(null)
      setStarsError((err && err.message) || t.starsInvoiceError)
      return
    }

    const onClosed = (e) => settleStars(e && e.status, invoiceLink)
    starsClosedRef.current = onClosed
    try {
      if (typeof tg.onEvent === 'function') tg.onEvent('invoiceClosed', onClosed)
    } catch { /* событие недоступно — обработает колбэк/промис openInvoice */ }

    try {
      const maybe = tg.openInvoice(invoiceLink, (status) => settleStars(status, invoiceLink))
      if (maybe && typeof maybe.then === 'function') {
        maybe.then(s => settleStars(s, invoiceLink)).catch(() => settleStars('failed', invoiceLink))
      }
    } catch {
      settleStars('failed', invoiceLink)
    }
  }

  // Отмена заявки (кнопка «Отмена» / крестик ✕ с подтверждением): сервер
  // помечает заявку failed и мгновенно возвращает адрес в пул. Возвращаемся
  // к выбору сети (шаг 2) — тариф остаётся выбранным.
  const doCancelPayment = async () => {
    if (!payment || cancelling) return
    setCancelling(true)
    try {
      await cancelPaymentRequest(payment.id)
      stopTimers()
      setPayment(null)
      setPaymentError(null)
      setStatus(null)
      setPendingHint(null)
      setProcessingWindow(false)
      setSubEnd(null)
      setStep(2)
    } catch (err) {
      // Отмена не прошла — показываем ошибку на шаге 3 (модалка разблокируется,
      // чтобы юзер не оказался в ловушке).
      setPaymentError(`${t.cancelPaymentFailed} ${err.message}`)
    } finally {
      setCancelling(false)
      setConfirmingCancel(false)
    }
  }

  // Остановка всех таймеров (при оплате/истечении/закрытии).
  const stopTimers = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  // Общий обработчик результата проверки (кнопка + повторные проверки):
  // paid → успех, expired/failed → закрытие окна. Возвращает true, если
  // результат «конечный» (окно пора закрывать).
  const applyResult = useCallback((data) => {
    if (data.status === 'paid') {
      setStatus('paid')
      setStep(4)
      setSubEnd(data.subscriptionEnd || null)
      setProcessingWindow(false)
      stopTimers()
      getMe()
        .then(me => {
          if (me?.subscription_end) setSubscriptionEnd(me.subscription_end)
          setAccessFlags(me)
        })
        .catch(() => { /* фоновая ошибка не критична */ })
      return true
    }
    if (data.status === 'expired' || data.status === 'failed') {
      setStatus('expired')
      setProcessingWindow(false)
      stopTimers()
      return true
    }
    return false
  }, [setSubscriptionEnd, setAccessFlags, stopTimers])

  useEffect(() => () => stopTimers(), [stopTimers])

  // При размонтировании модалки отвязываем обработчик invoiceClosed — иначе
  // уведомление об оплате ушло бы в несуществующий компонент.
  useEffect(() => () => detachStarsListener(), [])

  // При входе на шаг 3 автоматически создаём заявку.
  useEffect(() => {
    if (step === 3 && !payment && !creatingPayment && !paymentError && selectedPlan && selectedNetwork) {
      createPayment()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, payment, creatingPayment, paymentError])

  // ── Опрос статуса заявки ────────────────────────────────────────────────
  const pollStatus = useCallback(async () => {
    if (!payment || checkingRef.current) return
    checkingRef.current = true
    // Фоновый опрос НЕ трогает видимый `checking` — иначе каждые 5с строка
    // «Проверяем оплату» мерцала бы и двигала страницу. checking остаётся
    // только для ручного нажатия кнопки «Проверить оплату».
    try {
      const data = await getPaymentStatus(payment.id)
      if (data.status === 'paid') {
        setStatus('paid')
        setStep(4)
        setSubEnd(data.subscriptionEnd || null)
        setProcessingWindow(false)
        stopTimers()
        // Обновляем дату подписки и доступность типов счёта в шапке профиля
        try {
          const me = await getMe()
          if (me?.subscription_end) setSubscriptionEnd(me.subscription_end)
          setAccessFlags(me)
        } catch { /* фоновая ошибка не критична */ }
      } else if (data.status === 'expired' || data.status === 'failed') {
        setStatus('expired')
        setProcessingWindow(false)
        stopTimers()
      }
    } catch (err) {
      // временная сетевая ошибка — просто попробуем в следующий тик
    } finally {
      checkingRef.current = false
      setChecking(false)
    }
  }, [payment, setSubscriptionEnd, setAccessFlags])

  // ── «Я отправил платёж»: принудительная проверка сети ───────────────────
  // В отличие от фонового поллинга, кнопка зовёт POST /confirm: сервер сам
  // запускает verify_payment.py, который прямо сейчас опрашивает сеть заявки
  // и, если платёж обнаружен, зачисляет его мгновенно (без ожидания цикла
  // монитора). Даёт понятный фидбек: найден / ещё не найден / ошибка проверки.
  const handleConfirmPayment = useCallback(async () => {
    if (!payment || checkingRef.current) return
    checkingRef.current = true
    setChecking(true)
    setPendingHint(null)
    // Окно «оплата в обработке» открывается сразу: минимальная информация о
    // платеже + индикатор проверки + выход через поддержку / отмену. Кнопка
    // «Написать в поддержку» заблокирована до истечения заявки — таймер на ней
    // это тот же отсчёт, что и у окна оплаты (secondsLeft): платёж обычно
    // подтверждается за пару минут, поддержка нужна только если с платежом
    // реально что-то не так. При истечении окно закрывается и показывается
    // статус «заявка истекла» с кнопкой поддержки.
    setProcessingWindow(true)
    try {
      const data = await confirmPaymentRequest(payment.id)
      if (!applyResult(data)) {
        // Проверка выполнилась, но платёж пока не засчитан. Уведомление
        // «Платёж пока не обнаружен в сети» намеренно НЕ показываем — оно
        // пугает пользователя, хотя платёж в пути и будет подтверждён.
        // Показываем только реальную ошибку проверки (если сервер её вернул);
        // повторные проверки продолжат искать платёж автоматически.
        if (data.verifyError) {
          setPendingHint(String(t.paymentCheckFailedDetail || t.paymentCheckFailed).replace('{error}', String(data.verifyError).slice(0, 160)))
        }
      }
    } catch (err) {
      setPendingHint(`${t.paymentCheckFailed} ${err.message}`)
    } finally {
      checkingRef.current = false
      setChecking(false)
    }
  }, [payment, t, applyResult])

  // ── Повторные проверки сети, пока окно «оплата в обработке» открыто ──────
  // Первая проверка по кнопке могла пройти ДО того, как платёж дошёл до сети
  // (см. verify.log: balance=17.94, а деньги пришли следом). Чтобы засчитать
  // его без ожидания фонового монитора (который на сервере может быть не
  // запущен), каждые VERIFY_RETRY_MS снова вызываем POST /confirm — сервер
  // заново опрашивает сеть. Прекращаем по оплате/истечению или после
  // MAX_VERIFY_RETRIES (~2 минуты).
  useEffect(() => {
    if (!processingWindow || !payment) return
    verifyRetriesRef.current = 0
    const iv = setInterval(async () => {
      if (checkingRef.current) return
      if (verifyRetriesRef.current >= MAX_VERIFY_RETRIES) { clearInterval(iv); return }
      verifyRetriesRef.current++
      checkingRef.current = true
      try {
        const data = await confirmPaymentRequest(payment.id)
        if (!applyResult(data) && data.verifyError) {
          setPendingHint(String(t.paymentCheckFailedDetail || t.paymentCheckFailed)
            .replace('{error}', String(data.verifyError).slice(0, 160)))
        }
      } catch (err) {
        // временная сетевая ошибка — просто попробуем на следующем тике
      } finally {
        checkingRef.current = false
      }
    }, VERIFY_RETRY_MS)
    return () => clearInterval(iv)
  }, [processingWindow, payment, t, applyResult])

  // ── Поддержка (после истечения заявки) ───────────────────────────────────
  // Собираем сообщение с контекстом заявки, копируем в буфер и открываем
  // чат @fund1ngsupport. Остальная логика поддержки — позже.
  const openSupport = () => {
    setSupportOption(null)
    setSupportText('')
    setSupportCopied(false)
    setSupportSent(false)
    setSupportSending(false)
    setShowSupport(true)
  }

  const closeSupport = () => setShowSupport(false)

  const SUPPORT_OPTION_KEYS = [null, 'supportOpt1', 'supportOpt2', 'supportOpt3']

  const sendToSupport = async () => {
    if (!payment || supportSending) return
    // Подтягиваем id пользователя для контекста (не критично, если нет сети)
    let uid = ''
    try {
      const me = await getMe()
      if (me?.id) uid = String(me.id)
    } catch { /* без id сообщение всё равно полезно */ }
    const problem = supportOption
      ? t[SUPPORT_OPTION_KEYS[supportOption]]
      : (supportText.trim() || t.supportOpt3)
    const lines = [
      t.supportMsgHello,
      '',
      `${t.supportMsgUser}: ${uid || '—'}`,
      `${t.supportMsgRequest}: #${payment.id} (${payment.planCode}, ${payment.network})`,
      `${t.supportMsgAddress}: ${payment.address}`,
      `${t.supportMsgAmount}: ${payment.amountCoin} ${payment.currency}`,
      '',
      `${t.supportMsgProblem}: ${problem}`,
    ]
    if (supportText.trim() && supportOption) lines.push(`${t.supportMsgDetails}: ${supportText.trim()}`)
    const message = lines.join('\n')

    // Автоотправка: выбранный вариант + контекст уходят в чат поддержки через
    // бэкенд (POST /api/support/send) — юзеру ничего копировать не нужно.
    setSupportSending(true)
    setSupportSent(false)
    setSupportCopied(false)
    try {
      await sendSupportMessage(message)
      setSupportSent(true)
      return
    } catch {
      // Бэкенд недоступен — фолбек: копируем текст и открываем чат Telegram.
    } finally {
      setSupportSending(false)
    }

    let copied = false
    try {
      await navigator.clipboard.writeText(message)
      copied = true
    } catch {
      const ta = document.createElement('textarea')
      ta.value = message
      document.body.appendChild(ta)
      ta.select()
      try { copied = document.execCommand('copy') } catch { copied = false }
      document.body.removeChild(ta)
    }
    setSupportCopied(copied)
    const tg = window.Telegram?.WebApp
    if (tg && typeof tg.openTelegramLink === 'function') tg.openTelegramLink(SUPPORT_TELEGRAM)
    else window.open(SUPPORT_TELEGRAM, '_blank')
  }

  // Всегда держим свежую версию pollStatus для интервалов/кнопки.
  useEffect(() => {
    pollStatusRef.current = pollStatus
  }, [pollStatus])

  useEffect(() => {
    if (!payment) return
    // Обратный отсчёт до expires_at заявки
    const expires = new Date(payment.expiresAt.replace(' ', 'T') + 'Z')
    const tick = () => {
      const left = Math.max(0, Math.floor((expires.getTime() - Date.now()) / 1000))
      setSecondsLeft(left)
      if (left <= 0) {
        clearInterval(timerRef.current)
        timerRef.current = null
        setStatus(s => (s && s !== 'paid') ? 'expired' : s)
        setProcessingWindow(false)
      }
    }
    tick()
    timerRef.current = setInterval(tick, 1000)

    // Периодическая проверка статуса
    pollRef.current = setInterval(() => pollStatusRef.current(), POLL_SEC)

    return () => stopTimers()
  }, [payment?.id, stopTimers])

  const handleNext = () => {
    if (step === 1 && selectedPlan) {
      setStep(2)
    } else if (step === 2 && selectedNetwork) {
      setStep(3)
    }
  }

  const handleBack = () => {
    if (step === 3) {
      setStep(2)
      setPayment(null)
      setPaymentError(null)
      setStatus(null)
      setPendingHint(null)
      setProcessingWindow(false)
      setSubEnd(null)
    } else if (step === 2) {
      setStep(1)
      setSelectedNetwork(null)
      setStarsError(null)
    } else if (step === 4) {
      onClose()
    }
  }

  const getTitle = () => {
    if (starsPhase === 'paid') return t.starsPaid
    if (step === 1) return t.extendSubscription
    if (step === 2) return t.networkSelectTitle
    if (step === 3) return t.completePayment
    if (step === 4) return t.paymentConfirmed
    return t.extendSubscription
  }

  const planLabel = (plan) => {
    // Известный код тарифа → локаль из словаря. Иначе сервер хранит только
    // русское название (title_ru): EN-юзеру кириллицу не показываем — для
    // нестандартного тарифа это сырой code (или title_en, если появится).
    const known = t[PLAN_LABEL_KEYS[plan?.code]]
    if (known) return known
    if (plan?.title_en) return plan.title_en
    if (language === 'ru') return plan?.title_ru || plan?.code || ''
    return plan?.code || ''
  }

  const handleCopyAddress = () => {
    if (!payment?.address) return
    navigator.clipboard.writeText(payment.address)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCopyAmount = () => {
    if (!payment?.amountCoin) return
    const amt = Number(payment.amountCoin)
    const str = Number.isInteger(amt) ? String(amt) : amt.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
    navigator.clipboard.writeText(str)
    setCopiedAmount(true)
    setTimeout(() => setCopiedAmount(false), 2000)
  }

  const fmtCountdown = (sec) => {
    if (sec == null) return '--:--'
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const networkName = (key) => NETWORKS.find(n => n.key === key)?.name || key || '—'
  const networkSub = (key) => NETWORKS.find(n => n.key === key)?.sub || ''

  // Дата окончания подписки (бэкенд отдаёт 'YYYY-MM-DD') → 'ДД.ММ.ГГГГ'
  const fmtDate = (s) => {
    if (!s) return ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-')
      return `${d}.${m}.${y}`
    }
    const dt = new Date(s)
    return isNaN(dt) ? String(s) : dt.toLocaleDateString()
  }

  // Сумма к показу без лишних нулей (23 → '23', 3.14159 → '3.1416')
  const amountStrOf = (pay) => {
    if (!pay) return ''
    const amt = Number(pay.amountCoin)
    return Number.isInteger(amt) ? String(amt) : amt.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
  }

  // ── Шаг 1: выбор тарифа ─────────────────────────────────────────────────
  const renderStep1 = () => (
    <div>
      <p style={{ fontSize: 14, color: '#8E8E93', marginBottom: 20, lineHeight: 1.5 }}>
        {t.subscriptionSelectPlanDesc}
      </p>
      {loadError && (
        <div style={{ fontSize: 13, color: '#F87171', marginBottom: 14, lineHeight: 1.5 }}>
          ⚠️ {loadError}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plans.map((plan) => {
          const isDemo = plan.type === 'demo'
          const isSelected = selectedPlan?.code === plan.code
          return (
            <button
              key={plan.code}
              onClick={() => setSelectedPlan(plan)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 18px',
                borderRadius: 14,
                background: isSelected
                  ? (isDemo ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255, 107, 53, 0.12)')
                  : 'rgba(255,255,255,0.04)',
                border: isSelected
                  ? (isDemo ? '2px solid #6366F1' : '2px solid #34D399')
                  : '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%',
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>
                  {planLabel(plan)}
                </div>
                <div style={{ fontSize: 12, color: isDemo ? '#818CF8' : '#34D399', fontWeight: 600, marginTop: 3 }}>
                  {isDemo ? t.planDemoHint : t.planRealHint}
                </div>
                {/* Арбитраж входит во ВСЕ подписки, включая демо-счёт. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5, fontSize: 11.5, color: '#10B981', fontWeight: 600 }}>
                  <Bot size={13} /> {t.planIncludesArb}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {/* Актуальная цена + зачёркнутая старая (акция «было дороже») */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 7 }}>
                  {plan.old_price_usdt != null && plan.old_price_usdt > 0 && (
                    <span style={{
                      fontSize: 13, color: '#8E8E93', fontWeight: 600,
                      textDecoration: 'line-through',
                    }}>
                      ${Number(plan.old_price_usdt).toFixed(plan.old_price_usdt % 1 ? 2 : 0)}
                    </span>
                  )}
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#34D399' }}>
                    ${Number(plan.price_usdt).toFixed(plan.price_usdt % 1 ? 2 : 0)}
                  </span>
                </div>
                {plan.discount && (
                  <div style={{ fontSize: 12, color: '#34D399', fontWeight: 600 }}>{plan.discount}</div>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Риск-дисклеймер (Волна 0.3, 2026-08-31): мелким, но читаемым текстом
          на экране подписки. Полный юр. блок — в LegalModal (футер приложения). */}
      <p style={{
        marginTop: 10, textAlign: 'center',
        fontSize: 11, color: '#7A7A7E', lineHeight: 1.5,
        maxWidth: 320, marginLeft: 'auto', marginRight: 'auto',
      }}>
        ⚠️ {t.legalRiskDisclaimer}
      </p>
    </div>
  )

  // ── Шаг 2: выбор сети / Telegram Stars ─────────────────────────────────
  // Блок «Покупка в один клик» (⭐) — обычная кнопка над списком сетей, вёрсткой
  // как у сетей, БЕЗ акцентного CTA и БЕЗ пометок о наценке/комиссии (решение
  // владельца 2026-09-05). Виден, когда админ не выключил способ оплаты и у
  // тарифа есть цена в звёздах (priceStars присылает бэкенд в getPlans).
  const starsForPlan = () => Number(selectedPlan?.priceStars) || 0
  const starsEnabled = () => starsForPlan() > 0 && appConfig.payments?.stars !== false

  const renderStarsBlock = () => {
    const n = starsForPlan()
    // Долларовый эквивалент звёзд — по КУРСУ ПОКУПКИ, то есть столько юзер
    // заплатит за N⭐ в приложении (priceStarsUsd присылает бэкенд в getPlans).
    // В авто-режиме ≈ цена тарифа — подпись честно показывает цену юзера, а не
    // нашу выручку (задача владельца 2026-10-03). В ручном (фиксированные ⭐) —
    // реальные деньги за эти звёзды (решение владельца 2026-09-06).
    const usd = Number(selectedPlan?.priceStarsUsd) || 0
    const usdLabel = usd > 0 ? '≈ $' + usd.toFixed(2).replace(/\.00$/, '') : ''
    const buying = starsPhase === 'busy' || starsPhase === 'activating'
    return (
      <div style={{ marginBottom: 16 }}>
        {/* Заголовок блока + описание. Число ⭐ больше не в шапке — оно на кнопке. */}
        <div style={{ margin: '0 2px 10px' }}>
          <div style={{ fontSize: 12.5, color: '#8E8E93', fontWeight: 600, letterSpacing: 0.3 }}>{t.starsQuickTitle}</div>
          <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 3 }}>{t.starsQuickDesc}</div>
        </div>
        <button
          onClick={handleStarsBuy}
          disabled={buying}
          aria-label={String(t.starsBuy).replace('{stars}', String(n))}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, padding: '15px 18px', borderRadius: 14, width: '100%',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
            cursor: buying ? 'default' : 'pointer',
            transition: 'all 0.2s',
            opacity: buying ? 0.7 : 1,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>⭐ Telegram Stars</span>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 7, whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: '#fff' }}>{n} ⭐</span>
            {usdLabel && <span style={{ fontSize: 12.5, color: '#8E8E93' }}>{usdLabel}</span>}
          </span>
        </button>
        {starsError && (
          <div style={{ background: 'rgba(248, 113, 113, 0.1)', borderRadius: 10, padding: '10px 12px', marginTop: 10, border: '1px solid rgba(248, 113, 113, 0.25)', fontSize: 12.5, color: '#F87171', lineHeight: 1.5 }}>
            ⚠️ {starsError}
          </div>
        )}
      </div>
    )
  }

  // Состояния Stars-оплаты («Оплачиваю…» / «Оплачено — активирую» / успех):
  // пока счёт открыт или идёт активация — вместо содержимого шага 2.
  const renderStarsState = () => {
    if (starsPhase === 'busy' || starsPhase === 'activating') {
      const label = starsPhase === 'busy' ? t.starsProcessing : t.starsActivating
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '32px 0' }}>
          <Loader2 size={28} color="#34D399" style={{ animation: 'spin 1s linear infinite' }} />
          <div style={{ fontSize: 14, color: '#8E8E93', textAlign: 'center' }}>{label}</div>
        </div>
      )
    }
    // starsPhase === 'paid' — успех (заголовок окна — starsPaid).
    const rows = [
      [t.subInfoPlan, planLabel(selectedPlan)],
      [t.subInfoAmount, `${starsForPlan()} ⭐`],
      [t.subInfoValidUntil, fmtDate(starsSubEnd)],
    ].filter(([, v]) => v)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '24px 0' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(52, 211, 153, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Check size={32} color="#34D399" />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', textAlign: 'center' }}>{t.starsPaid}</div>
        <div style={{ fontSize: 14, color: '#8E8E93', textAlign: 'center' }}>{t.paymentConfirmed}</div>
        <div style={{ width: '100%', background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map(([label, value]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 13, color: '#8E8E93' }}>{label}</span>
              <span style={{ fontSize: 13.5, color: '#fff', fontWeight: 600, textAlign: 'right' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderStep2 = () => {
    // Показываем только сети, включённые админом в конфиге приложения.
    const visibleNetworks = NETWORKS.filter(n => appConfig.payments?.[NETWORK_PAYMENT_KEY[n.key]] !== false)
    const starsOn = starsEnabled()
    if (visibleNetworks.length === 0 && !starsOn) {
      return <p style={{ fontSize: 14, color: '#8E8E93', marginBottom: 20, lineHeight: 1.5 }}>{t.noFreeAddress}</p>
    }
    return (
    <div>
      {starsOn && renderStarsBlock()}
      {visibleNetworks.length > 0 && (
        <>
          <p style={{ fontSize: 14, color: '#8E8E93', marginBottom: 16, lineHeight: 1.5 }}>{t.selectNetworkForUsdt}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleNetworks.map((network) => {
              const available = networkAvailable(network.key)
              const isSelected = selectedNetwork === network.key
              return (
                <button
                  key={network.key}
                  disabled={!available}
                  onClick={() => setSelectedNetwork(network.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 18px',
                    borderRadius: 14,
                    background: isSelected ? 'rgba(255, 107, 53, 0.12)' : 'rgba(255,255,255,0.04)',
                    border: isSelected ? '2px solid #34D399' : '1px solid rgba(255,255,255,0.06)',
                    cursor: available ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    width: '100%',
                    opacity: available ? 1 : 0.4,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{network.name}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#8E8E93' }}>{network.sub}</span>
                  </div>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
    )
  }

  // ── Шаг 3: оплата (адрес + сумма) ───────────────────────────────────────
  const renderStep3 = () => {
    if (creatingPayment) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '32px 0' }}>
          <Loader2 size={28} color="#34D399" style={{ animation: 'spin 1s linear infinite' }} />
          <div style={{ fontSize: 14, color: '#8E8E93' }}>{t.creatingPayment}</div>
        </div>
      )
    }

    if (paymentError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
          <div style={{ background: 'rgba(248, 113, 113, 0.1)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(248, 113, 113, 0.25)' }}>
            <div style={{ fontSize: 14, color: '#F87171', lineHeight: 1.5 }}>{paymentError}</div>
          </div>
        </div>
      )
    }

    if (!payment) return null

    const amount = Number(payment.amountCoin)
    const amountStr = Number.isInteger(amount) ? String(amount) : amount.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')

    return (
      <div>
        <p style={{ fontSize: 14, color: '#8E8E93', marginBottom: 16, lineHeight: 1.5 }}>
          {t.subscriptionSendPaymentDesc}
        </p>

        {/* Сумма (всегда в USDT) — по нажатию цифра копируется в буфер обмена.
            Сеть — компактным бейджем справа, в пустой части карточки, чтобы не
            плодить отдельные панели. */}
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '16px 18px', marginBottom: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{t.amountToSend}</span>
            {copiedAmount && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#34D399', fontSize: 12, fontWeight: 600 }}><Check size={14} />{t.copied}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <button
              onClick={handleCopyAmount}
              style={{ display: 'flex', alignItems: 'baseline', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#34D399' }}
            >
              <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'monospace' }}>
                {amountStr} {payment.currency}
              </span>
              {!copiedAmount && <Copy size={16} style={{ opacity: 0.6 }} />}
            </button>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: '#8E8E93', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', padding: '4px 9px', borderRadius: 8, whiteSpace: 'nowrap' }}>
              <Network size={13} style={{ color: '#F59E0B' }} />
              {networkName(payment.network || selectedNetwork)}
            </span>
          </div>
        </div>

        {/* Адрес */}
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '16px 18px', marginBottom: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{t.walletAddress}</span>
            <button onClick={handleCopyAddress} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#34D399', fontSize: 12, fontWeight: 600 }}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? t.copied : t.copy}
            </button>
          </div>
          <div style={{ fontSize: 13, color: '#fff', fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.5, background: 'rgba(0,0,0,0.3)', padding: '10px 12px', borderRadius: 10 }}>
            {payment.address}
          </div>
        </div>

        {/* Таймер окна оплаты */}
        <div style={{ background: 'rgba(255, 107, 53, 0.08)', borderRadius: 12, padding: '12px 16px', marginBottom: 12, border: '1px solid rgba(255, 107, 53, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, color: '#F59E0B', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={14} /> {t.paymentWindowLeft}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#F59E0B', fontFamily: 'monospace' }}>
            {fmtCountdown(secondsLeft)}
          </span>
        </div>

        {/* Статус проверки: постоянный, пока ждём оплату (без мерцания каждые 5с) */}
        {!status && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Loader2 size={14} color="#34D399" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 13, color: '#34D399' }}>{t.paymentPending}</span>
          </div>
        )}

        {/* Фидбек после нажатия «Я отправил платёж»: ещё не найден / ошибка проверки */}
        {pendingHint && (
          <div style={{ background: 'rgba(255, 107, 53, 0.08)', borderRadius: 12, padding: '12px 14px', marginBottom: 12, border: '1px solid rgba(255, 107, 53, 0.2)', fontSize: 13, color: '#F59E0B', lineHeight: 1.5 }}>
            {pendingHint}
          </div>
        )}

        <div style={{ background: 'rgba(255, 107, 53, 0.08)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(255, 107, 53, 0.2)' }}>
          <p style={{ fontSize: 13, color: '#10B981', margin: 0, lineHeight: 1.5 }}>{t.subscriptionWarning}</p>
        </div>

        {/* Заявка истекла — предлагаем обратиться в поддержку */}
        {status === 'expired' && (
          <div style={{ background: 'rgba(248, 113, 113, 0.08)', borderRadius: 12, padding: '14px 16px', marginTop: 12, border: '1px solid rgba(248, 113, 113, 0.25)' }}>
            <p style={{ fontSize: 13, color: '#F87171', margin: 0, lineHeight: 1.5, marginBottom: 12 }}>{t.supportExpired}</p>
            <button
              onClick={openSupport}
              style={{ width: '100%', padding: '12px', borderRadius: 12, background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 2px 8px rgba(74, 222, 128, 0.25)' }}
            >
              {t.supportContact}
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── Шаг 4: успех (подписка одобрена + минимальная информация) ────────────
  const renderStep4 = () => {
    const plan = plans.find(p => p.code === payment?.planCode)
    const planTitle = plan
      ? planLabel(plan)
      : planLabel({ code: payment?.planCode, title_ru: payment?.planTitle })
    const endStr = fmtDate(subEnd)
    const infoRows = [
      [t.subInfoPlan, planTitle],
      [t.subInfoNetwork, networkName(payment?.network)],
      [t.subInfoValidUntil, endStr],
    ].filter(([, v]) => v)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '24px 0' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(52, 211, 153, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Check size={32} color="#34D399" />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', textAlign: 'center' }}>{t.paymentActivated}</div>
        <div style={{ fontSize: 14, color: '#8E8E93', textAlign: 'center' }}>{t.paymentConfirmed}</div>

        {/* Минимальная информация о подписке */}
        <div style={{ width: '100%', background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {infoRows.map(([label, value]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 13, color: '#8E8E93' }}>{label}</span>
              <span style={{ fontSize: 13.5, color: '#fff', fontWeight: 600, textAlign: 'right' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderFooter = () => {
    if (starsPhase === 'busy' || starsPhase === 'activating') {
      return (
        <button disabled style={{ width: '100%', padding: '14px', borderRadius: 14, background: 'rgba(255,255,255,0.06)', color: '#8E8E93', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'default' }}>
          {starsPhase === 'busy' ? t.starsProcessing : t.starsActivating}
        </button>
      )
    }
    if (starsPhase === 'paid') {
      return (
        <button onClick={() => modalRef.current?.close()} style={{ width: '100%', padding: '14px', borderRadius: 14, background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 2px 8px rgba(74, 222, 128, 0.3)' }}>
          {t.continue}
        </button>
      )
    }
    if (step === 1) {
      return (
        <button onClick={handleNext} disabled={!selectedPlan} style={{ width: '100%', padding: '14px', borderRadius: 14, background: selectedPlan ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' : 'rgba(255,255,255,0.06)', color: selectedPlan ? '#fff' : '#636366', fontSize: 15, fontWeight: 700, border: 'none', cursor: selectedPlan ? 'pointer' : 'default', transition: 'all 0.2s' }}>
          {t.continue}
        </button>
      )
    }
    if (step === 2) {
      return (
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleBack} style={{ flex: 1, padding: '14px', borderRadius: 14, background: 'rgba(255,255,255,0.06)', color: '#8E8E93', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' }}>{t.back}</button>
          <button onClick={handleNext} disabled={!selectedNetwork} style={{ flex: 2, padding: '14px', borderRadius: 14, background: selectedNetwork ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' : 'rgba(255,255,255,0.06)', color: selectedNetwork ? '#fff' : '#636366', fontSize: 15, fontWeight: 700, border: 'none', cursor: selectedNetwork ? 'pointer' : 'default', transition: 'all 0.2s' }}>
            {t.continue}
          </button>
        </div>
      )
    }
    if (step === 3) {
      if (creatingPayment) {
        // Заявка ещё не создана — отменять нечего. Одна кнопка-заглушка;
        // при ошибке создания разблокируется футер с «Отмена».
        return (
          <div>
            <button disabled style={{ width: '100%', padding: '14px', borderRadius: 14, background: 'rgba(255,255,255,0.06)', color: '#636366', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'default' }}>
              {t.creatingPayment}
            </button>
          </div>
        )
      }
      if (paymentError) {
        // Ошибка создания заявки — возврат к выбору сети (адрес мог освободиться)
        return (
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleBack} style={{ flex: 1, padding: '14px', borderRadius: 14, background: 'rgba(255,255,255,0.06)', color: '#8E8E93', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' }}>{t.back}</button>
            <button onClick={handleBack} style={{ flex: 2, padding: '14px', borderRadius: 14, background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
              {t.continue}
            </button>
          </div>
        )
      }
      if (status === 'expired') {
        // Заявка истекла — адрес вернулся в пул, создаём новую
        return (
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => modalRef.current?.close()} style={{ flex: 1, padding: '14px', borderRadius: 14, background: 'rgba(255,255,255,0.06)', color: '#8E8E93', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' }}>{t.back}</button>
            <button onClick={handleBack} style={{ flex: 2, padding: '14px', borderRadius: 14, background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
              {t.createPayment}
            </button>
          </div>
        )
      }
      return (
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => setConfirmingCancel(true)}
            disabled={cancelling || status === 'paid'}
            style={{ flex: 1, padding: '14px', borderRadius: 14, background: 'rgba(255,255,255,0.06)', color: '#8E8E93', fontSize: 15, fontWeight: 700, border: 'none', cursor: (cancelling || status === 'paid') ? 'default' : 'pointer' }}
          >
            {cancelling ? t.cancellingPayment : t.cancel}
          </button>
          <button
            onClick={handleConfirmPayment}
            disabled={checking || status === 'paid' || cancelling}
            style={{ flex: 2, padding: '14px', borderRadius: 14, background: (checking || status === 'paid' || cancelling) ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', color: (checking || status === 'paid' || cancelling) ? '#636366' : '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: (checking || status === 'paid' || cancelling) ? 'default' : 'pointer', boxShadow: '0 2px 8px rgba(74, 222, 128, 0.3)' }}
          >
            {checking ? t.paymentChecking : t.confirmPayment}
          </button>
        </div>
      )
    }
    if (step === 4) {
      return (
        <button onClick={() => modalRef.current?.close()} style={{ width: '100%', padding: '14px', borderRadius: 14, background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 2px 8px rgba(74, 222, 128, 0.3)' }}>
          {t.continue}
        </button>
      )
    }
  }

  // Окно оплаты (адрес + сумма) «запираем»: нельзя закрыть случайным тапом по
  // подложке или Esc — только «Отменой» (с подтверждением) или «Проверить
  // оплату». На ошибке создания / истечении заявки — разблокируем.
  const lockPayment = step === 3 && !creatingPayment && payment && !paymentError && !status

  return (
    <Modal
      ref={modalRef}
      title={getTitle()}
      onClose={onClose}
      footer={renderFooter()}
      dismissable={!lockPayment}
      onHeaderClose={lockPayment ? () => setConfirmingCancel(true) : null}
    >
      {step === 1 && renderStep1()}
      {step === 2 && (starsPhase ? renderStarsState() : renderStep2())}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}

      {confirmingCancel && (
        <ConfirmModal
          title={t.cancelPaymentConfirmTitle}
          message={t.cancelPaymentConfirmMsg}
          confirmLabel={cancelling ? t.cancellingPayment : t.cancel}
          cancelLabel={t.back}
          onConfirm={doCancelPayment}
          onCancel={() => setConfirmingCancel(false)}
        />
      )}

      {/* Подтверждение закрытия окна «оплата в обработке» (кнопка «Отмена»):
          заявка НЕ отменяется, возвращаемся к шагу оплаты. */}
      {confirmCloseWindow && (
        <ConfirmModal
          title={t.closeWindowConfirmTitle}
          message={t.closeWindowConfirmMsg}
          confirmLabel={t.closeWindow}
          cancelLabel={t.back}
          onConfirm={() => {
            setProcessingWindow(false)
            setConfirmCloseWindow(false)
          }}
          onCancel={() => setConfirmCloseWindow(false)}
        />
      )}

      {/* Окно поддержки: выбор проблемы / свой текст → отправка в @fund1ngsupport.
          Рендерится в document.body (createPortal), а не внутри scroll-контейнера
          модалки — иначе на iOS Safari position:fixed ведёт себя как absolute
          относительно скролла, и окно не закрывает весь экран (обрезается). */}
      {showSupport && createPortal((
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px' }}
          onClick={closeSupport}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.92)' }} />
          <div
            onClick={e => e.stopPropagation()}
            style={{ position: 'relative', zIndex: 1, background: '#1C1C1E', borderRadius: 24, width: '100%', maxWidth: 360, boxShadow: '0 32px 80px rgba(0,0,0,0.8)', border: '1px solid rgba(52,211,153,0.2)', overflow: 'hidden' }}
          >
            <div style={{ height: 3, background: 'linear-gradient(90deg, #FF3B30, #34D399)' }} />
            <div style={{ padding: '22px 22px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 17, fontWeight: 700, color: '#fff', margin: '0 0 10px' }}>{t.supportTitle}</p>
              <p style={{ fontSize: 13, color: '#8E8E93', margin: '0 0 16px', lineHeight: 1.5 }}>{t.supportIntro}</p>

              {/* Варианты проблемы */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {[1, 2, 3].map(i => (
                  <button
                    key={i}
                    onClick={() => setSupportOption(i)}
                    style={{
                      textAlign: 'left', padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
                      fontSize: 13.5, lineHeight: 1.4, color: '#fff',
                      background: supportOption === i ? 'rgba(52,211,153,0.12)' : 'rgba(255,255,255,0.05)',
                      border: supportOption === i ? '1px solid #34D399' : '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    {t[['supportOpt1', 'supportOpt2', 'supportOpt3'][i - 1]]}
                  </button>
                ))}
              </div>

              {/* Свой текст */}
              <textarea
                value={supportText}
                onChange={e => setSupportText(e.target.value)}
                placeholder={t.supportPlaceholder}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'none', padding: '12px 14px',
                  borderRadius: 12, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff', fontSize: 13.5, lineHeight: 1.5, outline: 'none', marginBottom: 14,
                }}
              />

              {/* Фидбек: автоотправлено */}
              {supportSent && (
                <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12.5, color: '#34D399', lineHeight: 1.5 }}>
                  {t.supportSentOk}
                </div>
              )}
              {/* Фидбек после копирования (фолбек, если автоотправка не сработала) */}
              {supportCopied && (
                <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12.5, color: '#34D399', lineHeight: 1.5 }}>
                  {t.supportCopied}
                </div>
              )}

              {/* Кнопки */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={closeSupport}
                  disabled={supportSending}
                  style={{ flex: 1, padding: '13px', borderRadius: 13, fontSize: 14, fontWeight: 600, background: 'rgba(255,255,255,0.07)', color: '#8E8E93', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}
                >
                  {t.back}
                </button>
                <button
                  onClick={sendToSupport}
                  disabled={supportSending}
                  style={{ flex: 2, padding: '13px', borderRadius: 13, fontSize: 14, fontWeight: 700, background: supportSending ? 'rgba(52,211,153,0.35)' : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', color: '#fff', border: 'none', cursor: supportSending ? 'default' : 'pointer', boxShadow: '0 4px 16px rgba(52,211,153,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  {supportSending && <Loader2 size={15} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />}
                  {supportSending ? t.supportSending : t.supportContact}
                </button>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}
      {/* Окно «оплата в обработке»: появляется по «Я отправил платёж», держится
          до подтверждения/истечения заявки. Выход — через поддержку или отмену.
          Рендерится в document.body (createPortal) — иначе на iOS Safari окно
          «обрезается» scroll-контейнером модалки, и кнопки футера («Отмена» /
          «Я отправил платёж») остаются видны сквозь чёрную подложку. */}
      {processingWindow && payment && createPortal((
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px' }}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.92)' }} />
          <div
            style={{ position: 'relative', zIndex: 1, background: '#1C1C1E', borderRadius: 24, width: '100%', maxWidth: 360, boxShadow: '0 32px 80px rgba(0,0,0,0.8)', border: '1px solid rgba(255, 167, 38, 0.25)', overflow: 'hidden' }}
          >
            <div style={{ height: 3, background: 'linear-gradient(90deg, #FFB020, #FF6B35, #34D399)' }} />
            <div style={{ padding: '22px 22px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Заголовок + спиннер */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Loader2 size={20} color="#FFB020" style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{t.processingTitle}</span>
              </div>
              <p style={{ fontSize: 13, color: '#8E8E93', margin: '0 0 16px', lineHeight: 1.5 }}>{t.processingCheck}</p>

              {/* Минимальная информация о платеже */}
              <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 12.5, color: '#8E8E93' }}>{t.subInfoAmount}</span>
                  <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, textAlign: 'right' }}>{amountStrOf(payment)} {payment.currency}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 12.5, color: '#8E8E93' }}>{t.subInfoNetwork}</span>
                  <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, textAlign: 'right' }}>{networkName(payment.network)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 12.5, color: '#8E8E93' }}>{t.walletAddress}</span>
                  <span style={{ fontSize: 12, color: '#8E8E93', fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'right', maxWidth: '60%', lineHeight: 1.4 }}>{payment.address}</span>
                </div>
              </div>

              {/* Статус проверки: спиннер + (если сервер вернул ошибку) подсказка.
                  «Платёж пока не обнаружен в сети» намеренно НЕ показываем — платёж
                  в пути и будет подтверждён, это пугало пользователей без причины. */}
              {pendingHint ? (
                <div style={{ background: 'rgba(255, 107, 53, 0.08)', borderRadius: 12, padding: '12px 14px', marginBottom: 12, border: '1px solid rgba(255, 107, 53, 0.2)', fontSize: 13, color: '#F59E0B', lineHeight: 1.5 }}>
                  {pendingHint}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <Loader2 size={14} color="#FFB020" style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 13, color: '#FFB020' }}>{t.processingTitle}</span>
                </div>
              )}

              {/* Кнопки: отмена (с подтверждением) / поддержка (с таймером).
                  Таймер на кнопке поддержки — это таймер окна оплаты (secondsLeft):
                  кнопка блокируется, пока заявка не истекла. Платёж обычно
                  подтверждается за пару минут, поэтому поддержка нужна только
                  если с платежом реально что-то не так (см. статус «истекла»). */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setConfirmCloseWindow(true)}
                  style={{ flex: 1, padding: '13px', borderRadius: 13, fontSize: 14, fontWeight: 600, background: 'rgba(255,255,255,0.07)', color: '#8E8E93', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}
                >
                  {t.cancel}
                </button>
                <button
                  disabled={(secondsLeft ?? 0) > 0}
                  onClick={() => { setProcessingWindow(false); openSupport() }}
                  style={{
                    flex: 2, padding: '13px', borderRadius: 13, fontSize: 14, fontWeight: 700,
                    background: (secondsLeft ?? 0) > 0 ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
                    color: (secondsLeft ?? 0) > 0 ? '#636366' : '#fff',
                    border: (secondsLeft ?? 0) > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                    cursor: (secondsLeft ?? 0) > 0 ? 'default' : 'pointer',
                    boxShadow: (secondsLeft ?? 0) > 0 ? 'none' : '0 4px 16px rgba(52,211,153,0.3)',
                  }}
                >
                  {(secondsLeft ?? 0) > 0 ? `${t.supportContact} · ${fmtCountdown(secondsLeft)}` : t.supportContact}
                </button>
              </div>
              <p style={{ fontSize: 11.5, color: '#636366', margin: '10px 2px 0', textAlign: 'center', lineHeight: 1.4 }}>{t.processingCancelHint}</p>
            </div>
          </div>
        </div>
      ), document.body)}
    </Modal>
  )
}
